import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertTriangle, ArrowDownRight, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtCurrency } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { useCategories } from "@/contexts/CategoriesContext";
import { iconForCategoryName } from "@/lib/categoryIcons";
import { DOC_KIND_LABELS } from "@/lib/receiptScan";

// Review step for a receipt/screenshot/statement scan. Every row is editable and can be
// unticked before saving; saving goes through POST /expenses/bulk in one request.
// `scan` is the POST /expenses/scan response, or null when closed.
export default function ScanReviewDialog({ scan, onOpenChange, onSaved }) {
    const { user } = useAuth();
    const cur = user?.currency || "USD";
    const { categories, categoryNames } = useCategories();
    const [rows, setRows] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setRows((scan?.items || []).map((it, idx) => ({
            ...it, _key: idx, _selected: true, amount: String(it.amount),
        })));
    }, [scan]);

    const update = (key, patch) => setRows(rs => rs.map(r => (r._key === key ? { ...r, ...patch } : r)));
    const selected = rows.filter(r => r._selected);
    const allSelected = rows.length > 0 && selected.length === rows.length;
    const net = useMemo(() => selected.reduce(
        (s, r) => s + (parseFloat(r.amount) || 0) * (r.type === "income" ? 1 : -1), 0,
    ), [selected]);
    const foreign = rows.some(r => r.currency && r.currency !== cur);

    const save = async () => {
        if (selected.length === 0) { toast.error("Select at least one row to save"); return; }
        const bad = selected.find(r => !r.title?.trim() || !(parseFloat(r.amount) > 0) || !r.occurred_at);
        if (bad) { toast.error("Each selected row needs a title, an amount above zero, and a date"); return; }
        try {
            setSaving(true);
            await api.post("/expenses/bulk", {
                items: selected.map(r => ({
                    title: r.title.trim(),
                    amount: parseFloat(r.amount),
                    type: r.type,
                    category: r.category,
                    payment_method: r.payment_method || null,
                    notes: r.notes || null,
                    occurred_at: new Date(r.occurred_at).toISOString(),
                })),
            });
            toast.success(selected.length === 1 ? "Saved 1 entry" : `Saved ${selected.length} entries`);
            onSaved?.();
            onOpenChange(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to save");
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={!!scan} onOpenChange={onOpenChange}>
            <DialogContent data-testid="scan-review-dialog" className="sm:max-w-2xl rounded-2xl max-h-[90vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl">
                        Review {rows.length === 1 ? "1 transaction" : `${rows.length} transactions`}
                    </DialogTitle>
                    <DialogDescription>
                        Read from your {(DOC_KIND_LABELS[scan?.document_kind] || "image").toLowerCase()}. Check the details, then save.
                    </DialogDescription>
                </DialogHeader>

                {foreign && (
                    <div className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="scan-currency-warning">
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>Some amounts look like a different currency than your {cur} setting. They'll be saved as {cur} as-is — convert them first if needed.</span>
                    </div>
                )}

                {rows.length > 1 && (
                    <label className="flex items-center gap-2 text-xs font-medium text-slate-600 px-1">
                        <Checkbox
                            checked={allSelected}
                            onCheckedChange={v => setRows(rs => rs.map(r => ({ ...r, _selected: !!v })))}
                            data-testid="scan-select-all"
                        />
                        Select all
                    </label>
                )}

                <div className="flex-1 overflow-y-auto space-y-2 pr-1 -mr-1" data-testid="scan-rows">
                    {rows.map(r => {
                        const isIncome = r.type === "income";
                        const mismatch = r.currency && r.currency !== cur;
                        return (
                            <div
                                key={r._key}
                                className={`rounded-xl border p-3 space-y-2 transition-opacity ${r._selected ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 opacity-60"}`}
                                data-testid="scan-row"
                            >
                                <div className="flex items-center gap-2">
                                    <Checkbox checked={r._selected} onCheckedChange={v => update(r._key, { _selected: !!v })} aria-label="Include this row" />
                                    <button
                                        type="button"
                                        onClick={() => update(r._key, { type: isIncome ? "expense" : "income" })}
                                        title="Switch between expense and income"
                                        className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold shrink-0 ${isIncome ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}
                                        data-testid="scan-row-type"
                                    >
                                        {isIncome ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                                        {isIncome ? "Income" : "Expense"}
                                    </button>
                                    <Input
                                        value={r.title} onChange={e => update(r._key, { title: e.target.value })}
                                        className="h-9 rounded-lg flex-1 min-w-0" placeholder="Title" data-testid="scan-row-title"
                                    />
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_1.4fr] gap-2 pl-6">
                                    <div className="relative">
                                        <Input
                                            type="number" step="0.01" value={r.amount}
                                            onChange={e => update(r._key, { amount: e.target.value })}
                                            className={`h-9 rounded-lg ${mismatch ? "pr-12" : ""}`} data-testid="scan-row-amount"
                                        />
                                        {mismatch && (
                                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-amber-600">{r.currency}</span>
                                        )}
                                    </div>
                                    <Input
                                        type="date" value={r.occurred_at}
                                        onChange={e => update(r._key, { occurred_at: e.target.value })}
                                        className="h-9 rounded-lg" data-testid="scan-row-date"
                                    />
                                    <Select value={categoryNames.includes(r.category) ? r.category : undefined} onValueChange={v => update(r._key, { category: v })}>
                                        <SelectTrigger className="h-9 rounded-lg col-span-2 sm:col-span-1" data-testid="scan-row-category"><SelectValue placeholder="Category" /></SelectTrigger>
                                        <SelectContent className="max-h-72">
                                            {categoryNames.map(c => {
                                                const { icon: CatIcon, color } = iconForCategoryName(categories, c);
                                                return (
                                                    <SelectItem key={c} value={c}>
                                                        <span className="inline-flex items-center gap-2"><CatIcon className="w-3.5 h-3.5" style={{ color }} />{c}</span>
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {r.notes && <p className="pl-6 text-[11px] text-slate-500 line-clamp-2">{r.notes}</p>}
                            </div>
                        );
                    })}
                </div>

                <DialogFooter className="flex-row items-center justify-between sm:justify-between gap-2 pt-2">
                    <span className="text-xs text-slate-500">
                        {selected.length} selected · net{" "}
                        <span className={`font-semibold ${net >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmtCurrency(net, cur)}</span>
                    </span>
                    <div className="flex gap-2">
                        <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button data-testid="scan-save" disabled={saving || selected.length === 0} onClick={save} className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5">
                            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            Save {selected.length || ""}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
